import classNames from "classnames"
import React, { useRef } from "react"
import type { SwiperRef } from "swiper/react"
import { Swiper, SwiperSlide } from "swiper/react"

import { Title2 } from "@bounce/design-system"

import type { SliderItemProps } from "./SliderItem"
import { SliderItem } from "./SliderItem"
import type { PNImageProps } from "../../PNImage/PNImage"
import { SliderNavigation } from "../../Slider/SliderNavigation/SliderNavigation"

export type PostSliderProps = {
  items: SliderItemProps[]
  itemWidth?: number
  imgixParams?: PNImageProps["imgixParams"]
  sliderTitle?: string
  sectionRef?: React.Ref<HTMLElement>
  footer?: React.ReactNode
  onNextSlide?: () => void
  titleLevel?: 2 | 3
  className?: string
}

export const PostSlider = ({
  items,
  sliderTitle,
  footer,
  imgixParams,
  sectionRef,
  onNextSlide,
  titleLevel = 2,
  className,
}: PostSliderProps) => {
  const swiperRef = useRef<SwiperRef>(null)

  const next = () => {
    swiperRef.current?.swiper.slideNext()
    onNextSlide?.()
  }

  const prev = () => {
    swiperRef.current?.swiper.slidePrev()
  }

  return (
    <section
      ref={sectionRef}
      className={classNames("relative mx-auto flex w-full max-w-screen-xl flex-col", className)}>
      {!!sliderTitle && (
        <Title2 level={titleLevel} className="px-6 text-black">
          {sliderTitle}
        </Title2>
      )}
      <div className="mt-14 md:mt-16 lg:mt-20">
        <Swiper
          ref={swiperRef}
          slidesPerView="auto"
          spaceBetween={18}
          slidesOffsetBefore={18}
          slidesOffsetAfter={0}
          tag="ul"
          watchSlidesProgress
          rewind>
          {items.map(post => (
            <SwiperSlide
              key={post.title}
              className="!w-[342px] md:!w-[381px] lg:!w-[646px]"
              style={{ height: "auto" }}
              tag="li">
              <SliderItem {...post} imgixParams={imgixParams} titleLevel={titleLevel === 3 ? 4 : 3} />
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="mt-8 flex items-center justify-between gap-4 px-6 md:mt-10 md:px-8 lg:mt-[52px] lg:px-6">
          {footer}
          <SliderNavigation onClickNext={next} onClickPrev={prev} sliderItemSize={items.length} />
        </div>
      </div>
    </section>
  )
}
